<template lang="pug">
Sheet(v-model:open="open")
  SheetTrigger(as-child)
    Button.text-xs.h-6(type="button" size="sm" variant="outline") 周期记录
  SheetContent(side="right" class="flex h-full w-[min(96vw,800px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[800px]")
    SheetHeader(class="shrink-0 border-b px-5 py-4 pr-12 sm:px-7")
      SheetTitle 服务周期记录
      SheetDescription 用户 {{ '#' + userId }} · 服务 {{ '#' + serviceId }}。显示已归档的周期，时间为北京时间。
    div(class="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-7" :aria-busy="loading")
      p.text-sm.text-muted-foreground(v-if="loading && !records.length" role="status") 正在加载周期记录…
      div.rounded-md.border.p-3.text-sm(v-if="error" class="border-destructive/30 bg-destructive/5" role="alert")
        p.text-destructive {{ error }}
        Button.mt-2(type="button" size="sm" variant="outline" :disabled="loading" @click="loadRecords(records.length > 0)") 重试
      p.text-sm.text-muted-foreground(v-if="!loading && !error && !records.length") 暂无已归档周期。
      article.rounded-lg.border.p-4.text-sm(v-for="record in records" :key="record.id")
        div.flex.flex-wrap.items-baseline.justify-between.gap-2
          h3.font-medium {{ formatDate(record.period_start) }} → {{ formatDate(record.period_end) }}
          span.text-xs.text-muted-foreground {{ '#' + record.id }}
        dl.mt-3.grid.gap-x-6.gap-y-3(class="sm:grid-cols-2")
          div
            dt.text-xs.text-muted-foreground 实际结束
            dd.mt-1 {{ formatDate(record.ended_at) }}
          div
            dt.text-xs.text-muted-foreground 结束原因
            dd.mt-1.break-all {{ record.end_reason }}
          div
            dt.text-xs.text-muted-foreground 标准流量 / 配额
            dd.mt-1 {{ $utils.trFormat(record.nor_gb) }} / {{ formatQuota(record.quote) }}
          div
            dt.text-xs.text-muted-foreground 中继流量 / 配额
            dd.mt-1 {{ $utils.trFormat(record.pre_gb) }} / {{ formatQuota(record.pre_quota) }}
          div
            dt.text-xs.text-muted-foreground 总用量
            dd.mt-1 {{ $utils.trFormat(record.nor_gb + record.pre_gb) }}
          div
            dt.text-xs.text-muted-foreground 套餐 / 计费模式
            dd.mt-1 {{ '#' + record.plan_id }} / {{ record.billing_mode }}
        details.mt-4.border-t.pt-3
          summary.cursor-pointer.text-xs.text-muted-foreground(class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring") 全部字段
          pre.mt-3.overflow-x-auto.rounded-md.bg-muted.p-3.text-xs {{ JSON.stringify(record, null, 2) }}
      Button.w-full(v-if="nextBeforeId !== null && !error" type="button" variant="outline" :disabled="loading" @click="loadRecords(true)") {{ loading ? '加载中…' : '加载更早周期' }}
    SheetFooter(class="shrink-0 border-t px-5 py-4 sm:px-7")
      span.mr-auto.text-xs.text-muted-foreground(role="status") 已加载 {{ records.length }} 期{{ records.length && nextBeforeId === null && !loading && !error ? ' · 已全部加载' : '' }}
      Button(type="button" variant="outline" :disabled="loading" @click="loadRecords(false)") 刷新
      Button(type="button" @click="open = false") 关闭
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import type { ServicePeriod, ServicePeriodHistory } from '~/types/servicePeriod'

const props = defineProps<{ userId: number, serviceId: number }>()
const open = ref(false)
const records = ref<ServicePeriod[]>([])
const nextBeforeId = ref<string | null>(null)
const loading = ref(false)
const error = ref('')
const dayjs = useDayjs()
const { $utils } = useNuxtApp()
let requestId = 0

function formatDate(value: string) {
  return dayjs(value).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss.SSS')
}

function formatQuota(value: number | null) {
  if (value === null) return '未记录'
  if (value === -1) return '无限'
  return $utils.trFormat(value)
}

async function loadRecords(append: boolean) {
  const id = ++requestId
  const { userId, serviceId } = props
  const beforeId = append ? nextBeforeId.value : null
  loading.value = true
  error.value = ''
  if (!append) {
    records.value = []
    nextBeforeId.value = null
  }
  try {
    const response = await useAPI().get(`users/${userId}/services/${serviceId}/billing_periods`, {
      params: { limit: 50, ...(beforeId === null ? {} : { before_id: beforeId }) },
    }) as ServicePeriodHistory
    if (id !== requestId) return
    records.value = append ? [...records.value, ...response.periods] : response.periods
    nextBeforeId.value = response.next_before_id
  }
  catch {
    if (id === requestId) error.value = '加载周期记录失败，请重试。'
  }
  finally {
    if (id === requestId) loading.value = false
  }
}

watch([open, () => props.userId, () => props.serviceId], () => {
  ++requestId
  records.value = []
  nextBeforeId.value = null
  loading.value = false
  error.value = ''
  if (open.value) void loadRecords(false)
}, { flush: 'sync' })

onBeforeUnmount(() => { ++requestId })
</script>
